import { isAfter, subDays } from 'date-fns';

import {
  GetB2bCompanyDocumentsResponse,
  GetB2bDashboardActiveCampaignsResponse,
  GetB2bDashboardAffiliatesResponse,
  GetB2bDashboardMatchingContributionsData,
  GetB2bDashboardSavingsResponse,
} from '@Common/types/bo/b2b/companies';

import { generateData } from '@sh4res/bo-common/components/B2bCompanyDashboard/mock_data_generator';

const currentYear = new Date().getFullYear();

export const mockOutstanding = generateData({
  startValue: 135000,
  maxIncrement: (date) => {
    if (date.getDate() > 25) {
      if ([4, 5, 6, 9, 10, 11].includes(date.getMonth())) {
        return 500;
      }
      return 100;
    }
    return 20;
  },
  minIncrement: 0,
  days: 365,
  maxValue: 9999999,
  minValue: 135000,
});

export const mockInvested = generateData({
  startValue: 56.23,
  maxIncrement: () => 1.4,
  minIncrement: -1.5,
  days: 365,
  maxValue: 123,
  minValue: 37,
});

export const mockEnrolment = generateData({
  startValue: 38,
  maxIncrement: () => 0.015,
  minIncrement: -0.01,
  days: 365,
  maxValue: 92,
  minValue: 31,
});

export const reduceMockData = (
  data: { date: Date; value: number }[],
  days: number,
  totalValues = 24,
) => {
  const arr = data.filter((entry) => isAfter(entry.date, subDays(new Date(), days)));
  if (totalValues >= arr.length) {
    return arr;
  }

  const result = [];
  const step = (arr.length - 1) / (totalValues - 1);

  for (let value = 0; value < totalValues; value++) {
    result.push(arr[Math.round(value * step)]);
  }

  return result;
};

export const mockMatchingContribution: GetB2bDashboardMatchingContributionsData = {
  [String(currentYear - 2)]: {
    totalPotentialAmount: 51034_00 + 56034_00 + 10350_00,
    alreadyPaid: 51034_00,
    awaitingCompany: 56034_00,
    notDistributed: 10350_00,
  },
  [String(currentYear - 1)]: {
    totalPotentialAmount: 35516_00 + 51034_00 + 14350_00,
    alreadyPaid: 35516_00,
    awaitingCompany: 51034_00,
    notDistributed: 14350_00,
  },
  [String(currentYear)]: {
    totalPotentialAmount: 33034_45 + 56000_00 + 9965_55,
    alreadyPaid: 33034_45,
    awaitingCompany: 56000_00,
    notDistributed: 9965_55,
  },
};

export const mockSavings: GetB2bDashboardSavingsResponse = {
  id: 1,
  data: {
    total_savings: 100000,
    per_product: [
      { name: 'PERECO-I Soles', amount: 56000 },
      { name: 'PEI Soles', amount: 33034.45 },
      { name: 'PERO', amount: 9965.55 },
    ],
    per_fund: [
      { name: 'Fund A', amount: 45000 },
      { name: 'Fund B', amount: 35000 },
      { name: 'Fund C', amount: 20000 },
    ],
  },
};

export const mockActiveCampaigns: GetB2bDashboardActiveCampaignsResponse = {
  id: 1,
  data: [
    {
      campaign_id: 28,
      campaign_type: 'voluntary_payment',
      status: 'in_progress',
      amount: 350000,
      answer_rate: 0.6,
      days_remaining: 8,
      temporal_progress: 0.75,
      start_date: new Date('2024-01-15'),
      end_date: new Date('2024-03-15'),
    },
    {
      campaign_id: 31,
      campaign_type: 'matching_contribution',
      status: 'in_progress',
      amount: 125000,
      answer_rate: 0.45,
      days_remaining: 22,
      temporal_progress: 0.4,
      start_date: new Date('2024-02-01'),
      end_date: new Date('2024-04-30'),
    },
  ],
};

export const mockLatestDocuments: GetB2bCompanyDocumentsResponse = [
  {
    id: 1,
    document_name: 'Contract Amendment Q3 2024.pdf',
    document_url: 'https://example.com/documents/contract-amendment-q3-2024.pdf',
    type: 'other',
    source: 'bo',
    created_at: new Date('2024-09-15T10:30:00Z'),
    updated_at: new Date('2024-09-15T10:30:00Z'),
    is_archived: false,
    company_id: 1,
    document_comment: null,
    is_generated: false,
    task_id: null,
    b2b_campaign_id: null,
    savings_funds_request_id: null,
  },
  {
    id: 2,
    document_name: 'Employee Benefits Report Sept 2024.xlsx',
    document_url: 'https://example.com/documents/employee-benefits-report-sept-2024.xlsx',
    type: 'other',
    source: 'bo',
    created_at: new Date('2024-09-10T14:20:00Z'),
    updated_at: new Date('2024-09-10T14:20:00Z'),
    is_archived: false,
    company_id: 1,
    document_comment: null,
    is_generated: false,
    task_id: null,
    b2b_campaign_id: null,
    savings_funds_request_id: null,
  },
  {
    id: 3,
    document_name: 'Compliance Audit August 2024.pdf',
    document_url: 'https://example.com/documents/compliance-audit-august-2024.pdf',
    type: 'other',
    source: 'bo',
    created_at: new Date('2024-08-28T09:15:00Z'),
    updated_at: new Date('2024-08-28T09:15:00Z'),
    is_archived: false,
    company_id: 1,
    document_comment: null,
    is_generated: false,
    task_id: null,
    b2b_campaign_id: null,
    savings_funds_request_id: null,
  },
];

export const mockAffiliates: GetB2bDashboardAffiliatesResponse = {
  id: 1,
  data: {
    affiliate_arbitrages: 12,
    affiliate_volontary_deposits: 15,
  },
};
